import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {
  BenchlingExpressionFunction,
  BenchlingExpressionType,
} from 'benchling-expressions/BenchlingExpressionTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {assertAllAccessible, assertIsAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import StructuredTableFormOutsideNotebook from 'notebook/structured-tables/StructuredTableFormOutsideNotebook';
import {processDetailsURL} from 'processes/dao/ProcessManagementDAO';
import {ALLOWED_ROUTING_OPERATORS_BY_FUNCNAME} from 'processes/modals/router-config/RouterConfigConstants';
import {ProcessTableLocation} from 'processes/ProcessConstants';
import RoutingRoutesDisplay, {
  EndNodeAndBlockingExpression,
  MapperAndBlockingExpression,
  SpecialExpressionType,
} from 'processes/routers/RoutingRoutesDisplay';
import {createOutputTableWithOptionalProcess} from 'processes/tables/createOutputTable';
import {createTaskTableFromTasks} from 'processes/tables/createTaskTable';
import {ProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import {Schema} from 'schemas/SchemaConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Accordion from 'ui-library/Accordion';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import * as Colors from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import APIUtil from 'util/APIUtil';

import {FixFailedRoutingAndMappingModalQuery} from './__generated/FixFailedRoutingAndMappingModalQuery';
import {RoutingErrorRecord} from './FixFailedRoutingAndMappingConstants';

type ProcessTask = ExtractByTypename<FixFailedRoutingAndMappingModalQuery['processTask'], 'ProcessTaskNode'>;

type ProcessOutput = ProcessTask['outputs'][number];

type BlockingExpression = ExtractByTypename<
  ProcessTask['processAsyncRecords'][number]['asyncData'],
  'ProcessStartAutomatedMappingRecordNode'
>['blockedEndNodesAndExpressions'][number]['blockingExpression'];

type BlockingRouter = ExtractByTypename<
  ProcessTask['processAsyncRecords'][number]['asyncData'],
  'ProcessStartAutomatedMappingRecordNode'
>['blockedMappersAndExpressions'][number]['blockingRouters'][number];

export interface SelectDownstreamNodesPaneProps {
  asyncRecord: RoutingErrorRecord;
  itemsToMap: Array<ProcessTask> | Array<ProcessOutput>;
  mapperIdsToDownstreamSchemaName: {[id: string]: string};
  itemIdsToSelectedMapperIds: {[id: string]: Array<string>};
  itemIdsToSelectedEndNodeIds: {[id: string]: Array<string>};
  onChangeItemIdsToSelectedMapperIds: (itemIdsToSelectedMapperIds: {[id: string]: Array<string>}) => void;
  onChangeItemIdsToSelectedEndNodeIds: (itemIdsToSelectedEndNodeIds: {[id: string]: Array<string>}) => void;
  task: ProcessTask;
  selectorsById: SchemaFieldSelectorsDict;
  allSchemasById: SchemaById;
}

interface RouteItemSectionProps {
  item: ProcessTask | ProcessOutput;
  task: ProcessTask;
  asyncRecord: RoutingErrorRecord;
  selectedMapperIds: Array<string>;
  selectedEndNodeIds: Array<string>;
  onChangeSelectedMapperIds: (selectedMapperIds: Array<string>) => void;
  onChangeSelectedEndNodeIds: (selectedEndNodeIds: Array<string>) => void;
  mapperIdsToDownstreamSchemaName: {[id: string]: string};
  selectorsById: SchemaFieldSelectorsDict;
  baseSchema: Schema;
  allSchemasById: SchemaById;
}

interface FixRoutingErrorAccordionProps extends RouteItemSectionProps {
  isOpenByDefault?: boolean;
}

function createTableForTask(
  task: ProcessTask,
  selectorsById: SchemaFieldSelectorsDict
): ProcessTaskStructuredTable {
  return createTaskTableFromTasks({
    tasks: [
      {
        ...task,
        schema: {
          ...assertIsAccessible(task.taskSchema),
          statusLifecycle: undefined, // we don't grab the full statuslifecycle node in this code path
        },
      },
    ],
    tableLocation: ProcessTableLocation.FIELD_MAPPING_MODAL,
    locked: true,
    selectorsById,
  });
}

function createTableForOutput(
  output: ProcessOutput,
  task: ProcessTask,
  selectorsById: SchemaFieldSelectorsDict
): ProcessOutputStructuredTable {
  const taskSchema = assertIsAccessible(task.taskSchema);
  assert(taskSchema.outputSchema != null, 'Cannot make output table for task schema without output schema');
  return createOutputTableWithOptionalProcess({
    processTaskSchema: {...taskSchema, outputSchema: taskSchema.outputSchema},
    outputs: [{...output, task: {id: task.id, humanId: task.humanId}}],
    metadata: {tableLocation: ProcessTableLocation.FIELD_MAPPING_MODAL},
    locked: true,
    selectorsById,
  });
}

function getExpressionFunctionOrSpecialType(
  blockingExpression: BlockingExpression
): BenchlingExpressionFunction | SpecialExpressionType {
  if (isInaccessible(blockingExpression)) {
    return SpecialExpressionType.INACCESSIBLE;
  }
  if (blockingExpression.expressionJson == null) {
    return SpecialExpressionType.DEFAULT_EXPRESSION;
  }
  assert(
    blockingExpression.expressionJson.type === BenchlingExpressionType.FUNCTION,
    'Expected top level expression to be a function'
  );
  return blockingExpression.expressionJson;
}

function RouteItemSection(props: RouteItemSectionProps): JSX.Element {
  const blockedEndNodes = new Set(
    props.asyncRecord.asyncData.blockedEndNodesAndExpressions.map(({blockedEndNode}) => blockedEndNode)
  );
  const endNodeIdsToNames = [...blockedEndNodes].reduce(
    (acc, {id, name}) => {
      acc[id] = name;
      return acc;
    },
    {} as {[id: string]: string}
  );
  const dropdownDisplay =
    props.selectedMapperIds.length === 0 && props.selectedEndNodeIds.length === 0
      ? ''
      : [
          ...props.selectedMapperIds.map((mapperId) => props.mapperIdsToDownstreamSchemaName[mapperId]),
          props.selectedEndNodeIds.map((endNodeId) => endNodeIdsToNames[endNodeId]),
        ].join(', ');

  const structuredTable = useMemo(
    () =>
      props.item.__typename === 'ProcessTaskNode'
        ? createTableForTask(props.item, props.selectorsById)
        : createTableForOutput(props.item, props.task, props.selectorsById),
    [props.item, props.task, props.selectorsById]
  );

  let flowchartTaskSchema: Schema | null = null;
  // Assume a single root to resolve the flowchart task schema: workflow tasks don't split/pool today and FFM
  // is not available for procedure tasks. FOLLOW-UP (before enabling split/pool for workflow tasks): consume
  // the per-mapping root the server would expose on the failed-mapping record instead of ``rootTasks[0]``.
  if (props.task.rootTasks.length > 0) {
    const rootTaskSchema = props.task.rootTasks[0].taskSchema;
    if (!isInaccessible(rootTaskSchema)) {
      flowchartTaskSchema = props.allSchemasById[rootTaskSchema.id] ?? null;
    }
  }

  const mapperAndBlockingExpressions: Array<
    MapperAndBlockingExpression & {blockingRouters: Array<BlockingRouter>}
  > = useMemo(
    () =>
      props.asyncRecord.asyncData.blockedMappersAndExpressions.map(
        ({schemaMapper, blockingExpressions, blockingRouters}) => {
          assert(blockingExpressions.length <= 1, 'Expected only one expression to block a mapper');

          if (blockingExpressions.length === 0) {
            return {
              isSchemaMapper: true,
              schemaMapper,
              expression: SpecialExpressionType.NO_BLOCKING_ROUTERS,
              blockingRouters,
            };
          }
          return {
            isSchemaMapper: true,
            schemaMapper,
            expression:
              flowchartTaskSchema != null
                ? getExpressionFunctionOrSpecialType(blockingExpressions[0])
                : SpecialExpressionType.INACCESSIBLE,
            blockingRouters,
          };
        }
      ),
    [props.asyncRecord, flowchartTaskSchema]
  );

  const endNodesAndBlockingExpressions: Array<EndNodeAndBlockingExpression> = useMemo(
    () =>
      props.asyncRecord.asyncData.blockedEndNodesAndExpressions.map(
        ({blockedEndNode, blockingExpression, fromSchemas}) => ({
          isSchemaMapper: false,
          endNode: {id: blockedEndNode.id, name: blockedEndNode.name},
          expression:
            flowchartTaskSchema != null
              ? getExpressionFunctionOrSpecialType(blockingExpression)
              : SpecialExpressionType.INACCESSIBLE,
          fromSchemas,
        })
      ),
    [props.asyncRecord, flowchartTaskSchema]
  );

  const containsChainedRouters = mapperAndBlockingExpressions
    .map((mapperAndBlockingExpression) => mapperAndBlockingExpression.blockingRouters.length > 1)
    .includes(true);

  const flowchartOnClick: () => void = () => window.open(processDetailsURL(props.task.process.id));

  const menuOptions = [
    ...props.asyncRecord.asyncData.blockedMappersAndExpressions.map(({schemaMapper, blockingRouters}) => (
      <Option
        key={schemaMapper.id}
        value={schemaMapper.id}
        onSelect={(mapperId: string) => {
          props.onChangeSelectedMapperIds(
            produce(props.selectedMapperIds, (draftSelectedMapperIds) => {
              const idx = draftSelectedMapperIds.findIndex(
                (selectedMapperId) => selectedMapperId === mapperId
              );
              if (idx >= 0) {
                draftSelectedMapperIds.splice(idx, 1);
              } else {
                draftSelectedMapperIds.push(mapperId);
              }
            })
          );
        }}
      >
        {props.mapperIdsToDownstreamSchemaName[schemaMapper.id]}
      </Option>
    )),
    [...blockedEndNodes].map((blockedEndNode) => (
      <Option
        key={blockedEndNode.id}
        value={blockedEndNode.id}
        onSelect={(endNodeId: string) => {
          props.onChangeSelectedEndNodeIds(
            produce(props.selectedEndNodeIds, (draftSelectedEndNodeIds) => {
              const idx = draftSelectedEndNodeIds.findIndex(
                (selectedEndNodeId) => selectedEndNodeId === endNodeId
              );
              if (idx >= 0) {
                draftSelectedEndNodeIds.splice(idx, 1);
              } else {
                draftSelectedEndNodeIds.push(endNodeId);
              }
            })
          );
        }}
      >
        {blockedEndNode.name}
      </Option>
    )),
  ];

  return (
    <div>
      {containsChainedRouters && (
        <div className={css(styles.containsChainedRoutersInfo)}>
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            hideIcon={true}
            text="Review the flowchart to ensure the selected node(s) match the intended path."
            supportingInfo="Selecting the next node(s) may skip over several routers that are in sequence."
            action={{
              actionType: InlineBannerActionType.CUSTOM,
              actionText: 'View flowchart',
              actionOnClick: flowchartOnClick,
            }}
          />
        </div>
      )}
      <div className={css(styles.selectNodeDropdownSection)}>
        <div>
          <strong>Select step(s):</strong>
        </div>
        <div className={css(styles.nodeDropdown)}>
          <SelectDropdown
            displayName={dropdownDisplay}
            closeOnInsideClick={false}
            menu={
              <Menu
                isMulti={true}
                value={[...props.selectedMapperIds, ...props.selectedEndNodeIds]}
                width={MenuWidth.FULL}
              >
                {menuOptions}
              </Menu>
            }
            dataTestAttrs={{
              component: 'RouteItemSection',
              element: 'selectNodesDropdown',
            }}
          />
        </div>
      </div>
      <div>
        <div className={css(styles.fieldsHeaderText)}>
          Fields for <BenchChip id={props.item.id} size={ChipSize.SMALL} isLink={false} />
        </div>
        <StructuredTableFormOutsideNotebook
          cid={`routing-decision-${props.item.id}`}
          structuredTable={structuredTable}
          updateHandler={() => {}}
          locked={true}
          hasPrimarySamples={false}
        />
      </div>
      <div>
        <div className={css(styles.routingRoutesHeader)}>
          <span className={css(styles.routingRoutesHeaderText)}>Conditional logic</span>
          <Tooltip
            text={`This logic is applied to the ${
              APIUtil.isProcessTaskSchema(props.baseSchema.id) ? 'task' : 'output'
            } fields`}
          >
            <BenchlingIcon className={css(styles.infoIcon)} icon="info-circle" />
          </Tooltip>
        </div>
        <RoutingRoutesDisplay
          mapperOrEndNodeAndBlockingExpressions={[
            ...mapperAndBlockingExpressions,
            ...endNodesAndBlockingExpressions,
          ]}
          mapperIdsToDownstreamSchemaName={props.mapperIdsToDownstreamSchemaName}
          selectorsById={props.selectorsById}
          allSchemasById={props.allSchemasById}
          allowedRoutingFunctionsByName={ALLOWED_ROUTING_OPERATORS_BY_FUNCNAME}
          flowchartTaskSchema={flowchartTaskSchema}
        />
      </div>
    </div>
  );
}

function FixRoutingErrorAccordion(props: FixRoutingErrorAccordionProps): JSX.Element {
  const doneIconStyle =
    props.selectedMapperIds.length > 0 || props.selectedEndNodeIds.length > 0
      ? styles.accordionSectionCompleted
      : styles.accordionSectionIncomplete;

  return (
    <Accordion
      isOpenByDefault={props.isOpenByDefault}
      header={
        <>
          <span className={css(styles.accordionHeaderName)}>
            <strong>{props.item.humanId}</strong>
          </span>
          <BenchlingIcon icon="done" className={css(doneIconStyle)} />
        </>
      }
      dataTestAttrs={{
        component: 'FixRoutingErrorAccordion',
        element: 'root',
        key: props.item.id,
      }}
    >
      <RouteItemSection
        item={props.item}
        task={props.task}
        selectorsById={props.selectorsById}
        asyncRecord={props.asyncRecord}
        selectedMapperIds={props.selectedMapperIds}
        selectedEndNodeIds={props.selectedEndNodeIds}
        mapperIdsToDownstreamSchemaName={props.mapperIdsToDownstreamSchemaName}
        onChangeSelectedMapperIds={props.onChangeSelectedMapperIds}
        onChangeSelectedEndNodeIds={props.onChangeSelectedEndNodeIds}
        baseSchema={props.baseSchema}
        allSchemasById={props.allSchemasById}
      />
    </Accordion>
  );
}

export default function SelectDownstreamNodesPane(props: SelectDownstreamNodesPaneProps): JSX.Element {
  const baseSchemas = assertAllAccessible(
    props.asyncRecord.asyncData.blockedMappersAndExpressions[0]?.schemaMapper.fromSchemas ?? []
  );
  assert(baseSchemas.length === 1, 'Expected routers to only route from a single node');
  const baseSchema = props.allSchemasById[baseSchemas[0].id];
  assert(baseSchema != null, 'Expected schema to be fetched');
  assert(
    props.asyncRecord.asyncData.blockedMappersAndExpressions.every(
      ({schemaMapper}) =>
        schemaMapper.fromSchemas.length === 1 &&
        assertIsAccessible(schemaMapper.fromSchemas[0]).id === baseSchema.id
    ),
    'Expected all mappers to have the same base schema'
  );

  return (
    <div {...getTestProps({component: 'SelectDownstreamNodesPane', element: 'root'})}>
      {props.itemsToMap.map((item, idx) => (
        <FixRoutingErrorAccordion
          key={item.id}
          isOpenByDefault={idx === 0}
          item={item}
          task={props.task}
          selectorsById={props.selectorsById}
          asyncRecord={props.asyncRecord}
          selectedMapperIds={props.itemIdsToSelectedMapperIds[item.id] ?? []}
          selectedEndNodeIds={props.itemIdsToSelectedEndNodeIds[item.id] ?? []}
          mapperIdsToDownstreamSchemaName={props.mapperIdsToDownstreamSchemaName}
          allSchemasById={props.allSchemasById}
          baseSchema={baseSchema}
          onChangeSelectedMapperIds={(newMapperIds) => {
            props.onChangeItemIdsToSelectedMapperIds(
              produce(props.itemIdsToSelectedMapperIds, (draftItemIdsToSelectedMapperIds) => {
                draftItemIdsToSelectedMapperIds[item.id] = newMapperIds;
              })
            );
          }}
          onChangeSelectedEndNodeIds={(newEndNodeIds) => {
            props.onChangeItemIdsToSelectedEndNodeIds(
              produce(props.itemIdsToSelectedEndNodeIds, (draftItemIdsToSelectedEndNodeIds) => {
                draftItemIdsToSelectedEndNodeIds[item.id] = newEndNodeIds;
              })
            );
          }}
        />
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  individualErrorSection: {
    borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
    ':last-child': {
      borderBottom: 'none',
    },
  },
  accordion: {
    width: '100%',
    boxShadow: 'none',
  },
  accordionIcon: {
    color: Colors.GRAY_DARK,
  },
  accordionSectionCompleted: {
    color: Colors.BLUE_LIGHT,
  },
  accordionSectionIncomplete: {
    color: Colors.GRAY,
  },
  accordionHeaderText: {
    marginTop: '16px',
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  accordionHeaderName: {
    marginLeft: '8px',
    marginRight: '8px',
  },
  fieldsHeaderText: {
    marginBottom: '8px',
  },
  nodeDropdown: {
    marginTop: '4px',
    width: '50%',
  },
  selectNodeDropdownSection: {
    marginBottom: '16px',
  },
  routingRoutesHeader: {
    marginTop: 16,
    marginBottom: 8,
  },
  infoIcon: {
    color: Colors.GRAY,
  },
  routingRoutesHeaderText: {
    marginRight: 4,
  },
  containsChainedRoutersInfo: {
    marginBottom: 16,
  },
});
